import { computed, watch, type Component } from 'vue';
import * as Sentry from '@sentry/vue';
import { hasBucketIconData, modelIconForRow } from '@/composables/modelIcons';
import { useModelRecordQuery } from '@/composables/api/useModelRecordQuery';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import type { ParentBucketRef } from '@/composables/searchInspectorRegistry';

/**
 * The mark drawn beside a parent bucket's name — static, dynamic, mixed or
 * vault — resolved the same way on every surface that shows one.
 *
 * Normally it costs nothing: a bucket's nested reference carries `bucket_type`
 * straight from the search document, so the row already knows. When it does
 * not, this fetches the bucket rather than guessing or drawing nothing, and
 * the card and the inspector heading now agree about that. They used to
 * disagree — the card drew no mark and the inspector fetched one — so the same
 * record could look different depending on where you saw it.
 *
 * ## Why the fallback is also an alert
 *
 * A reference with no `bucket_type` is not a normal state. It means either the
 * search document predates that field and the index is stale, or the bucket's
 * `bucket_type_id` was never backfilled — the column is written by MySQL
 * triggers and is nullable, so a row can genuinely sit there unclassified.
 * Both are data problems that fix themselves quietly on the next reindex and
 * otherwise never announce themselves, which is exactly the kind of thing
 * worth hearing about once rather than never.
 *
 * The reporting is bounded, because a broadly stale index would otherwise mean
 * one alert per bucket on every page: each bucket is reported once, and after
 * {@link REPORT_LIMIT} distinct buckets this stops. Five samples already say
 * what is wrong; the alert exists to say the condition EXISTS, not to count it.
 *
 * ## Who is sent on the request
 *
 * Only a user who can open buckets. Anyone else gets no mark for a typeless
 * reference, and no request. The reference is reported either way: a stale
 * index is the same problem whoever happens to be looking at it.
 */

/** Distinct buckets to report per page load before falling silent. */
const REPORT_LIMIT = 5;

const reportedBucketIds = new Set<string>();

/** Test seam: the reported set is module state, so a test must be able to empty it. */
export function resetParentBucketMarkReporting(): void {
    reportedBucketIds.clear();
}

function reportUntypedReference(bucketId: number | string): void {
    const key = String(bucketId);
    if (reportedBucketIds.has(key) || reportedBucketIds.size >= REPORT_LIMIT) {
        return;
    }
    reportedBucketIds.add(key);

    Sentry.captureMessage(
        'Bucket reference carries no bucket_type, so its mark needed a request',
        {
            level: 'warning',
            extra: {
                bucketId: key,
                meaning:
                    'The search document is stale, or buckets.bucket_type_id was never backfilled for this bucket.',
            },
        }
    );
}

/**
 * @param parentBucket The reference to draw a mark for, or null when the record has no parent.
 */
export function useParentBucketMark(parentBucket: () => ParentBucketRef | null) {
    /**
     * The id to fetch, or null when nothing needs fetching: no parent at all,
     * no id to fetch by, or a reference that already settles its own mark.
     */
    const unresolvedBucketId = computed<number | string | null>(() => {
        const parent = parentBucket();
        if (parent === null || parent.id === null || hasBucketIconData(parent.iconRow)) {
            return null;
        }

        return parent.id;
    });

    // The report is a side effect, so it watches the id rather than living
    // inside the computed that derives it — a computed re-runs whenever its
    // dependencies change, and reporting from one means the alert count
    // follows re-renders instead of following the data.
    watch(
        unresolvedBucketId,
        (bucketId) => {
            if (bucketId !== null) {
                reportUntypedReference(bucketId);
            }
        },
        { immediate: true }
    );

    const { canOpen } = useOpenRecordInInspector();
    const fetchedBucketId = computed(() => (canOpen('Bucket') ? unresolvedBucketId.value : null));
    const bucketQuery = useModelRecordQuery('Bucket', fetchedBucketId);

    /**
     * Null until the variant is actually known. Callers render nothing for a
     * null rather than the model-level Bucket icon, which is the STATIC
     * artwork and would label the record a static bucket.
     */
    const icon = computed<Component | null>(() =>
        modelIconForRow('Bucket', bucketQuery.data.value ?? parentBucket()?.iconRow)
    );

    return { icon };
}
